/**
 * Ground-truth fixtures: a build document paired with what the game actually reported for
 * it.
 *
 * Until the companion mod exists (deferred to after phase 3), `observed` blocks are
 * transcribed by hand from the in-game stat GUI. That screen exposes exactly the fields the
 * engine has to reproduce — `current_value`, `usable_value`, `dmg_multi`, `softcap`,
 * `hardcap`, `min` — which is what makes hand transcription viable rather than a poor
 * substitute.
 *
 * `dmgMulti` is the load-bearing one. `InCalcStatData.getCalculated()` splits a stat into
 * two fields:
 *
 *     float mu = 1;
 *     if (GetStat().getMultiUseType() == Stat.MultiUseType.MULTIPLICATIVE_DAMAGE) { mu = Multi; }
 *     return new StatData(this.id, calcValue(), mu);
 *
 * and `StatData.getValue()` returns the value *without* `mu`. An engine that folded MORE
 * modifiers into the value everywhere would match `currentValue` on most stats and be
 * silently wrong on the ~150 `MULTIPLICATIVE_DAMAGE` ones. Recording both fields is what
 * catches that.
 *
 * The `source` field is why this format outlives the transcription era: when the mod lands
 * it emits `"mod_dump"` into the same shape, so nothing captured now has to be re-entered.
 */
import { baseGearType } from "./queries.js";
import { validateBuild } from "./validate.js";
/**
 * Where an `observed` block was read from.
 *
 * `damage_log` is the mod's own per-hit breakdown, behind the `damage_messages` player config
 * and shown as a hover on the combat message. It is the richest capture available without the
 * companion mod: one hover carries base damage, every layer with its multiplier, every named
 * MORE and the final number, so it pins the whole damage pipeline rather than the one figure at
 * the end of it.
 */
export const OBSERVATION_SOURCES = ["stat_gui", "damage_log", "mod_dump"];
export const EXACT = { absolute: 1e-6, relative: 0 };
/** Half of the last digit the sheet prints, plus a hair for float representation noise. */
export const TWO_DECIMALS = { absolute: 0.005 + 1e-9, relative: 0 };
export const ONE_DECIMAL = { absolute: 0.05 + 1e-9, relative: 0 };
/**
 * Damage numbers are read off floating combat text and health bars, not a formatted stat row,
 * and the game truncates to an int at several points in `ValueCalculation`. Half a point is
 * the tightest honest bound for a transcribed hit; `baseValue` comes from a tooltip that
 * prints a plain integer, so it stays exact.
 */
export const HALF_POINT = { absolute: 0.5 + 1e-9, relative: 0 };
/**
 * The band an `(int)` cast in the log leaves behind.
 *
 * `Base Damage`, `Final Damage` and `Total Combined Damage` are all printed through a cast,
 * not a rounding — `(int) this.data.getOriginalNumber(...).number` and
 * `info.dmgmap.get(...).intValue()` — so a printed 13041 means the game held something in
 * `[13041, 13042)`. That is not a symmetric tolerance, and treating it as `±0.5` would fail a
 * correct engine on any hit whose fraction happened to exceed one half. {@link compareTruncated}
 * tests the band instead; this constant only carries the float slack at its edges.
 */
export const TRUNCATION_SLACK = 1e-6;
/**
 * The default table: what a human reading a screen can honestly record.
 *
 * Used for `stat_gui` and `damage_log`, and as the fallback for any field a tighter source has
 * no opinion about.
 */
export const FIELD_TOLERANCE = {
    currentValue: TWO_DECIMALS,
    dmgMulti: TWO_DECIMALS,
    usableValue: ONE_DECIMAL,
    softcap: EXACT,
    hardcap: EXACT,
    baseValue: EXACT,
    hit: HALF_POINT,
    crit: HALF_POINT,
    ailmentPerSecond: HALF_POINT,
    // The three endpoints are int-cast and go through {@link compareTruncated} instead, which
    // ignores this entry; it is here because the table is exhaustive over `ComparedField`.
    baseDamage: EXACT,
    finalDamage: EXACT,
    totalCombined: EXACT,
    // Everything in between is printed through `MMORPG.DECIMAL_FORMAT` ("0.00"), so half of the
    // last digit is the tightest honest bound — the same argument as the stat GUI's, for the same
    // `DecimalFormat`.
    layerAmount: TWO_DECIMALS,
    layerMultiplier: TWO_DECIMALS,
    moreMulti: TWO_DECIMALS,
};
/**
 * What the companion mod's rounding costs: it writes stat values to four decimals
 * (`ObservedExport.round`), so a recorded number is within half of the last digit of the float
 * the game actually held.
 */
const MOD_DUMP_ROUNDING = 0.00005;
/**
 * The gap between the game's arithmetic and this engine's, as a fraction of the value.
 *
 * Mine and Slash computes in 32-bit `float` from end to end — `StatData.v1`, every
 * `ExactStatData`, the whole container — and this engine computes in doubles. A single float
 * rounding is at most `2^-24` (about 6e-8) of the value, and a stat's number is the result of a
 * chain of them: a base, a sum of flats, a percent multiply, a MORE multiply, a level scale, a
 * clamp. A few dozen roundings puts the worst case near 3e-6; `1e-5` is that with room to
 * spare.
 *
 * This term is the reason a mod dump needs a *relative* bound rather than a tighter absolute
 * one. At 18 attack speed it allows 0.00023 where the stat GUI's rule allowed 0.005 — twenty
 * times stricter. At 50,000 health it allows 0.5, which sounds loose until you notice that one
 * float32 step at that magnitude is already 0.0039: the old flat 0.005 was asking for agreement
 * finer than the game can represent, and would have failed a correct engine rather than caught
 * a wrong one.
 */
const FLOAT32_DRIFT = 1e-5;
/**
 * A stat read straight out of the synced container, not off a screen.
 *
 * `{ i, v, m }` per stat, written by `UnitNbt.Save` — no formatting, no truncation, no
 * transcription. The only two things between that number and this comparison are the
 * exporter's four-decimal rounding and the float/double difference above.
 */
export const MOD_DUMP = {
    absolute: MOD_DUMP_ROUNDING + 1e-9,
    relative: FLOAT32_DRIFT,
};
/**
 * Per-source overrides. A field absent here falls through to {@link FIELD_TOLERANCE}.
 *
 * `softcap` and `hardcap` are left out on purpose even for a mod dump: they are integers read
 * off the stat registry rather than computed, so `EXACT` is already right and loosening them
 * would only hide a wrong cap in the ported table.
 *
 * The damage fields are left out too, and a reading that needs a different bound says so itself
 * rather than inheriting one here: `ObservedDamage.source` overrides the observation's. That is
 * the ordinary case now — the companion mod writes the stat sheet as a float-exact dump but
 * cannot read the damage log, so a fixture routinely carries `mod_dump` stats beside
 * `damage_log` numbers that are still a human transcribing an int-rounded hover.
 */
const SOURCE_OVERRIDES = {
    stat_gui: {},
    damage_log: {},
    mod_dump: {
        currentValue: MOD_DUMP,
        dmgMulti: MOD_DUMP,
        usableValue: MOD_DUMP,
    },
};
/**
 * How close this field has to be, given where the number came from.
 *
 * Two captures of the same stat are not equally precise, and comparing them as though they were
 * is the difference between a runner that catches a 0.1% error and one that cannot. A
 * transcription from the stat GUI is bounded by the screen's two decimals; a dump from the
 * companion mod is bounded by float arithmetic, which is three orders of magnitude tighter at
 * the magnitudes most stats live at.
 */
export function toleranceFor(field, source) {
    return SOURCE_OVERRIDES[source][field] ?? FIELD_TOLERANCE[field];
}
/** Which observed field a comparison is about. `currentValue` is `ComputedStat.value`. */
export const COMPARED_FIELDS = [
    "currentValue",
    "dmgMulti",
    "usableValue",
    "softcap",
    "hardcap",
    "baseValue",
    "hit",
    "crit",
    "ailmentPerSecond",
    "baseDamage",
    "layerAmount",
    "layerMultiplier",
    "moreMulti",
    "finalDamage",
    "totalCombined",
];
/**
 * Validates a fixture's build and, if an engine is supplied, diffs it against the
 * observation.
 *
 * With no calculator every stat comes back `unimplemented`. That is the honest answer while
 * the engine does not exist, and it is deliberately not `match` — a runner that went green
 * against an empty engine would be worse than no runner.
 */
/**
 * The build as the observation was taken: nothing up that the capture did not record.
 *
 * A fixture is the one place the closed-world reading is right. Everywhere else this project
 * plans — it offers you every effect your build could apply and assumes them on, because that is
 * the question a planner is for. Here the question is whether the engine reproduces a *measured*
 * sheet, and the character whose sheet was measured was not running the buffs the capture did not
 * list. Assuming them would put stats on the engine's side that the game never had, and the
 * mismatch would look like a porting bug.
 *
 * Set here rather than written into the exported document, so that loading the same capture into
 * the app gives you a build to work on rather than a snapshot of one instant.
 */
function measured(build) {
    return { ...build, config: { ...(build.config ?? {}), assumeEffects: "captured" } };
}
export function compareFixture(fixture, snapshot, calculator, options = {}) {
    // An explicit `options.tolerance` overrides everything, for a caller deliberately widening or
    // narrowing a run. Otherwise the capture's own source decides, per field.
    const tolerance = options.tolerance;
    const source = fixture.observed.source;
    const validateOptions = options.balanceId === undefined ? {} : { balanceId: options.balanceId };
    const diagnostics = validateBuild(fixture.build, snapshot, validateOptions);
    const computed = calculator ? calculator(measured(fixture.build), snapshot) : null;
    const comparisons = [];
    for (const observed of fixture.observed.stats) {
        const actual = computed?.get(observed.statId) ?? null;
        const ran = computed !== null;
        // A stat neither container holds is not unknown — the game has a defined answer for it:
        //
        //     public StatData getCalculatedStat(String guid) {
        //         if (getStats().stats == null) { this.initStats(); }
        //         return getStats().stats.getOrDefault(guid, new StatData(guid, 0, 1));
        //     }
        //
        // — Unit.java:104-109, and `initStats` only allocates an empty map, so the game's own
        // container is sparse exactly as the engine's is. Value 0 and dmgMulti 1 are therefore the
        // right comparison for a stat the engine never touched, not a `missing` report: a capture
        // that observed `armor: 0` on a bare level 1 agrees with an engine that has no armor entry.
        // `usableValue` and the caps are deliberately left out — those are properties of the
        // registered `Stat` rather than of a `StatData`, so an absent one really is unknown here.
        const absentValue = ran && actual === null ? 0 : actual?.value;
        const absentMulti = ran && actual === null ? 1 : actual?.dmgMulti;
        comparisons.push(compareField(observed.statId, "currentValue", observed.currentValue, absentValue, ran, tolerance, source));
        // The rest are optional in a capture: the sheet only shows `dmg_multi` on a
        // MULTIPLICATIVE_DAMAGE stat whose multiplier is not 1, `usable_value` on the four
        // `IUsableStat` stats, and no `softcap` row at all while nothing in the mod sets one.
        for (const [field, expected, got] of [
            ["dmgMulti", observed.dmgMulti, absentMulti],
            ["usableValue", observed.usableValue, actual?.usableValue],
            ["softcap", observed.softcap, actual?.softcap],
            ["hardcap", observed.hardcap, actual?.hardcap],
        ]) {
            if (expected !== undefined) {
                comparisons.push(compareField(observed.statId, field, expected, got, ran, tolerance, source));
            }
        }
    }
    compareDamage(fixture, snapshot, options.damage ?? null, tolerance, source, comparisons);
    diagnostics.push(...unrecordedContexts(fixture));
    diagnostics.push(...loggedWeaponMismatch(fixture, snapshot));
    return { name: fixture.name, source, diagnostics, comparisons };
}
/**
 * Checks that a logged hit was made with the weapon the capture describes.
 *
 * This exists because they were once not the same, and nothing said so. A capture reads the
 * *selected* hotbar slot, so a character with a scythe in slot 1 and a different sword in slot 2
 * exports the scythe — while the hits being compared against it were swung with the sword. Every
 * absolute number in that comparison is then about a weapon the build document does not contain,
 * and it looks exactly like an engine bug.
 *
 * A warning rather than an error: the reading is still worth keeping, and the ratios in it are
 * still meaningful. What it must not do is go unremarked.
 */
function loggedWeaponMismatch(fixture, snapshot) {
    const out = [];
    const equipped = fixture.build.gear ?? [];
    for (const [i, observed] of (fixture.observed.damage ?? []).entries()) {
        const logged = observed.weapon?.baseType;
        if (logged === undefined)
            continue;
        const slot = baseGearType(snapshot, logged)?.gearSlot;
        // Compare within the slot the logged weapon belongs to. Matching against the whole of `gear`
        // would pass a build that happens to carry the same base in the offhand, which is the very
        // confusion this is here to catch.
        const inSlot = equipped.filter((item) => baseGearType(snapshot, item.base)?.gearSlot === slot);
        if (inSlot.some((item) => item.base === logged))
            continue;
        const held = inSlot.length === 0 ? "nothing" : inSlot.map((item) => `\`${item.base}\``).join(", ");
        const where = observed.weapon?.hotbarSlot === undefined ? "" : ` (hotbar slot ${observed.weapon.hotbarSlot})`;
        out.push({
            severity: "warning",
            code: "logged-weapon-not-equipped",
            path: `observed.damage[${i}].weapon`,
            message: `The hit on \`${observed.spellId}\` was made with \`${logged}\`${where}, but the build's ` +
                `\`${slot ?? "unknown"}\` slot holds ${held}. The capture and the hit describe different ` +
                `characters, so every absolute number in this reading (base damage, flat damage) is ` +
                `comparing against gear that was not in hand. Re-export with the weapon that made the hit ` +
                `selected. Ratios and the layer structure are still meaningful.`,
        });
    }
    return out;
}
/**
 * Which build-document field produces each `StatCtxType` the game reports a contribution under.
 *
 * Only the types a document can express are listed. A capture whose `sources` name a type the
 * document is empty for is a capture taken by an exporter that did not know about it yet — and
 * the mismatch that follows is a *missing input*, not a wrong calculation. Saying so is the
 * whole point: three stats reading low because lunch was not recorded looks exactly like an
 * engine bug until something names the difference.
 */
const CONTEXT_FIELDS = {
    GEAR: { field: "gear", has: (b) => (b.gear ?? []).length > 0 },
    JEWEL: { field: "jewels", has: (b) => (b.jewels ?? []).length > 0 },
    AURA: { field: "auras", has: (b) => (b.auras ?? []).length > 0 },
    FOOD_BUFF: { field: "foodBuffs", has: (b) => (b.foodBuffs ?? []).length > 0 },
    POTION_EFFECT: { field: "exileEffects", has: (b) => (b.exileEffects ?? []).length > 0 },
    TALENT: { field: "tree.talents", has: (b) => (b.tree?.talents ?? []).length > 0 },
    ASCENDANCY: { field: "tree.ascendancy", has: (b) => (b.tree?.ascendancy ?? []).length > 0 },
    ATLAS: { field: "tree.atlas", has: (b) => (b.tree?.atlas ?? []).length > 0 },
    PASSIVES: { field: "character.schools", has: (b) => Object.keys(b.character.schools ?? {}).length > 0 },
    STAT_POINTS: {
        field: "character.statPoints",
        has: (b) => Object.keys(b.character.statPoints ?? {}).length > 0,
    },
    VANILLA_STAT_COMPAT: {
        field: "character.attributes",
        has: (b) => Object.keys(b.character.attributes ?? {}).length > 0,
    },
    ENCHANT_COMPAT: {
        field: "character.attributes",
        has: (b) => Object.keys(b.character.attributes ?? {}).length > 0,
    },
};
function unrecordedContexts(fixture) {
    const sources = fixture.observed.sources ?? [];
    if (sources.length === 0)
        return [];
    const out = [];
    const seen = new Set();
    for (const source of sources) {
        const known = CONTEXT_FIELDS[source.ctx];
        if (!known || seen.has(source.ctx) || known.has(fixture.build))
            continue;
        seen.add(source.ctx);
        const count = sources.filter((s) => s.ctx === source.ctx).length;
        out.push({
            severity: "warning",
            code: "capture-missing-context",
            path: known.field,
            message: `The capture records ${count} ${source.ctx} contribution(s) but \`${known.field}\` is empty, ` +
                `so the engine has nothing to reproduce them from. Any stat they touch will read low, and ` +
                `that is the capture missing an input rather than the engine being wrong. Re-export with ` +
                `a companion mod that records this.`,
        });
    }
    return out;
}
/**
 * Diffs the `observed.damage` block.
 *
 * Same posture as the stat comparison: with no calculator every reading is `unimplemented`,
 * never `match`. Only fields the capture actually recorded are compared, so a fixture holding
 * just a tooltip `baseValue` checks exactly that and claims nothing about the layer stack.
 */
function compareDamage(fixture, snapshot, calculator, tolerance, observationSource, out) {
    for (const observed of fixture.observed.damage ?? []) {
        const computed = calculator
            ? calculator(measured(fixture.build), snapshot, observed.spellId, observed)
            : undefined;
        const ran = calculator !== null;
        // A reading's own provenance wins over the observation's. See `ObservedDamage.source`.
        const source = observed.source ?? observationSource;
        for (const [field, expected, got] of [
            ["baseValue", observed.baseValue, computed?.baseValue],
            ["hit", observed.hit, computed?.hit],
            ["crit", observed.crit, computed?.crit],
        ]) {
            if (expected !== undefined) {
                out.push(compareField(observed.spellId, field, expected, got, ran, tolerance, source));
            }
        }
        for (const [ailment, expected] of Object.entries(observed.ailmentPerSecond ?? {})) {
            out.push(compareField(`${observed.spellId}/${ailment}`, "ailmentPerSecond", expected, computed?.ailmentPerSecond[ailment], ran, tolerance, source));
        }
        compareLog(observed, computed, ran, tolerance, source, out);
        compareAilmentEvents(observed, computed, ran, tolerance, source, out);
    }
}
/**
 * Walks the log's layer stack against the engine's, row by row.
 *
 * The point of doing it this way rather than comparing the totals is that a damage pipeline is a
 * product of a dozen terms: one wrong factor moves the end, and the end alone cannot say which.
 * Every row here is a named place to be wrong — `quake/physical.additive_damage` disagreeing is
 * an afternoon shorter than `quake` disagreeing.
 *
 * Rows the engine produced and the log did not are **not** reported. The log prints only layers
 * that actually did something and only MOREs that are not exactly 1, and the far commoner cause
 * of an extra engine row is a capture that did not transcribe every line. A missing row on the
 * engine's side is reported, because that one is always the engine's.
 */
function compareLog(observed, computed, ran, tolerance, source, out) {
    if (observed.totalCombined !== undefined) {
        out.push(compareTruncated(observed.spellId, "totalCombined", observed.totalCombined, computed?.totalCombined, ran));
    }
    if (observed.log === undefined)
        return;
    for (const event of observed.log) {
        // Keyed by element rather than by position: the hover prints the hit's own element first
        // and then the bonus ones in map order, and a capture that transcribed them in another
        // order should still line up.
        const mine = computed?.log?.find((e) => e.element === event.element);
        compareEvent(`${observed.spellId}/${event.element}`, event, mine, ran, tolerance, source, out);
    }
}
/**
 * The ailment half of a damage capture: the block that applied each one, and the proc that
 * released it.
 *
 * Keyed by ailment id rather than by position, for the reason the hit's blocks are keyed by
 * element — a capture is a human reading hovers in whatever order they scrolled past.
 *
 * Nothing here is keyed off `ailmentPerSecond`. The two record different things and a capture
 * can have either: that field is one second of a DoT ticking, this is the event that applied it.
 */
function compareAilmentEvents(observed, computed, ran, tolerance, source, out) {
    for (const event of observed.ailments ?? []) {
        const mine = computed?.ailments?.find((a) => a.ailment === event.ailment);
        const where = `${observed.spellId}/${event.ailment}`;
        if (event.applied !== undefined) {
            compareEvent(`${where}:applied`, event.applied, mine?.applied, ran, tolerance, source, out);
        }
        if (event.proc !== undefined) {
            compareEvent(`${where}:proc`, event.proc, mine?.proc, ran, tolerance, source, out);
        }
    }
}
/**
 * Walks one printed block — base, every layer row, every MORE, final — against the engine's.
 *
 * Shared by the hit's elements and by an ailment's two blocks because the log prints all of them
 * in the same shape, which is the whole reason the trace was built to match it.
 */
function compareEvent(where, event, mine, ran, tolerance, source, out) {
    out.push(compareTruncated(where, "baseDamage", event.baseDamage, mine?.baseDamage, ran));
    out.push(compareTruncated(where, "finalDamage", event.finalDamage, mine?.finalDamage, ran));
    for (const layer of event.layers) {
        // Side is part of the identity. `additive_damage` is written by both halves of the event
        // and the log prints the prefix precisely because the two rows are different rows.
        const got = mine?.layers.find((l) => l.layerId === layer.layerId && l.side === layer.side && l.element === layer.element);
        const id = `${where}.${layer.layerId}${layer.element === undefined ? "" : `>${layer.element}`}[${layer.side}]`;
        if (layer.amount !== undefined) {
            out.push(compareField(id, "layerAmount", layer.amount, got?.amount, ran, tolerance, source));
        }
        if (layer.multiplier !== undefined) {
            out.push(compareField(id, "layerMultiplier", layer.multiplier, got?.multiplier, ran, tolerance, source));
        }
    }
    for (const more of event.moreMultis) {
        out.push(compareField(`${where}.${more.statId}`, "moreMulti", more.multi, mine?.moreMultis.find((m) => m.statId === more.statId)?.multi, ran, tolerance, source));
    }
}
/**
 * Compares against a number the game printed through an `(int)` cast.
 *
 * A printed `n` means the game held something in `[n, n + 1)`, so the test is the band and not
 * a distance. `delta` is reported as the signed distance *outside* it — zero when the engine
 * agrees, negative when it came in under the band, positive when it overshot — so the runner's
 * "off by X, allowed Y" line stays readable for a bound that is not symmetric.
 */
function compareTruncated(statId, field, expected, actual, engineRan) {
    if (!engineRan) {
        return { statId, field, expected, actual: null, delta: null, allowed: null, status: "unimplemented" };
    }
    if (actual === undefined) {
        return { statId, field, expected, actual: null, delta: null, allowed: null, status: "missing" };
    }
    const low = expected - TRUNCATION_SLACK;
    const high = expected + 1 + TRUNCATION_SLACK;
    const delta = actual < low ? actual - expected : actual >= high ? actual - (expected + 1) : 0;
    return { statId, field, expected, actual, delta, allowed: 0, status: delta === 0 ? "match" : "mismatch" };
}
function compareField(statId, field, expected, actual, engineRan, tolerance, source) {
    if (!engineRan) {
        return { statId, field, expected, actual: null, delta: null, allowed: null, status: "unimplemented" };
    }
    if (actual === undefined) {
        return { statId, field, expected, actual: null, delta: null, allowed: null, status: "missing" };
    }
    const limit = tolerance ?? toleranceFor(field, source);
    const delta = actual - expected;
    const allowed = limit.absolute + Math.abs(expected) * limit.relative;
    return {
        statId,
        field,
        expected,
        actual,
        delta,
        allowed,
        status: Math.abs(delta) <= allowed ? "match" : "mismatch",
    };
}
/**
 * Parses and structurally checks a fixture file.
 *
 * Throws rather than returning diagnostics: a malformed fixture is a broken test, not a
 * finding about a character.
 */
export function parseFixture(value, origin) {
    const fail = (message) => {
        throw new Error(`${origin}: ${message}`);
    };
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return fail("expected a JSON object at the top level");
    }
    const root = value;
    const name = root["name"];
    if (typeof name !== "string" || name.length === 0)
        return fail("missing `name`");
    const build = root["build"];
    if (build === null || typeof build !== "object" || Array.isArray(build)) {
        return fail("missing `build` object");
    }
    const observedRaw = root["observed"];
    if (observedRaw === null || typeof observedRaw !== "object" || Array.isArray(observedRaw)) {
        return fail("missing `observed` object");
    }
    const observed = observedRaw;
    const source = observed["source"];
    if (typeof source !== "string" || !OBSERVATION_SOURCES.includes(source)) {
        return fail(`observed.source must be one of ${OBSERVATION_SOURCES.join(" | ")}, got ${JSON.stringify(source)}`);
    }
    const statsRaw = observed["stats"];
    if (!Array.isArray(statsRaw))
        return fail("observed.stats must be an array");
    const stats = statsRaw.map((raw, i) => {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
            return fail(`observed.stats[${i}] must be an object`);
        }
        const stat = raw;
        const statId = stat["statId"];
        if (typeof statId !== "string" || statId.length === 0) {
            return fail(`observed.stats[${i}].statId must be a non-empty string`);
        }
        const currentValue = stat["currentValue"];
        if (typeof currentValue !== "number" || !Number.isFinite(currentValue)) {
            return fail(`observed.stats[${i}].currentValue must be a finite number`);
        }
        return {
            statId,
            currentValue,
            ...optionalNumber(stat, "usableValue", i, fail),
            ...optionalNumber(stat, "dmgMulti", i, fail),
            ...optionalNumber(stat, "softcap", i, fail),
            ...optionalNumber(stat, "hardcap", i, fail),
        };
    });
    const damageRaw = observed["damage"];
    if (damageRaw !== undefined && !Array.isArray(damageRaw)) {
        return fail("observed.damage must be an array when present");
    }
    const damage = (damageRaw ?? []).map((raw, i) => {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
            return fail(`observed.damage[${i}] must be an object`);
        }
        const node = raw;
        const spellId = node["spellId"];
        if (typeof spellId !== "string" || spellId.length === 0) {
            return fail(`observed.damage[${i}].spellId must be a non-empty string`);
        }
        const perSecond = node["ailmentPerSecond"];
        const ailments = {};
        if (perSecond !== undefined) {
            if (perSecond === null || typeof perSecond !== "object" || Array.isArray(perSecond)) {
                return fail(`observed.damage[${i}].ailmentPerSecond must be an object`);
            }
            for (const [ailment, value] of Object.entries(perSecond)) {
                if (typeof value !== "number" || !Number.isFinite(value)) {
                    return fail(`observed.damage[${i}].ailmentPerSecond.${ailment} must be a finite number`);
                }
                ailments[ailment] = value;
            }
        }
        const logRaw = node["log"];
        if (logRaw !== undefined && !Array.isArray(logRaw)) {
            return fail(`observed.damage[${i}].log must be an array when present`);
        }
        const log = (logRaw ?? []).map((rawEvent, j) => parseEvent(rawEvent, `observed.damage[${i}].log[${j}]`, fail));
        const ailmentsRaw = node["ailments"];
        if (ailmentsRaw !== undefined && !Array.isArray(ailmentsRaw)) {
            return fail(`observed.damage[${i}].ailments must be an array when present`);
        }
        const ailmentEvents = (ailmentsRaw ?? []).map((rawAilment, j) => {
            const at = `observed.damage[${i}].ailments[${j}]`;
            if (rawAilment === null || typeof rawAilment !== "object" || Array.isArray(rawAilment)) {
                return fail(`${at} must be an object`);
            }
            const node2 = rawAilment;
            const ailment = node2["ailment"];
            if (typeof ailment !== "string" || ailment.length === 0) {
                return fail(`${at}.ailment must be a non-empty string`);
            }
            return {
                ailment,
                ...(node2["applied"] === undefined
                    ? {}
                    : { applied: parseEvent(node2["applied"], `${at}.applied`, fail) }),
                ...(node2["proc"] === undefined
                    ? {}
                    : { proc: parseEvent(node2["proc"], `${at}.proc`, fail) }),
            };
        });
        const readingSource = node["source"];
        if (readingSource !== undefined &&
            (typeof readingSource !== "string" ||
                !OBSERVATION_SOURCES.includes(readingSource))) {
            return fail(`observed.damage[${i}].source must be one of ${OBSERVATION_SOURCES.join(" | ")} when present`);
        }
        const effectsRaw = node["effects"];
        const effects = {};
        if (effectsRaw !== undefined) {
            if (effectsRaw === null || typeof effectsRaw !== "object" || Array.isArray(effectsRaw)) {
                return fail(`observed.damage[${i}].effects must be an object`);
            }
            for (const [id, stacks] of Object.entries(effectsRaw)) {
                if (typeof stacks !== "number" || !Number.isFinite(stacks)) {
                    return fail(`observed.damage[${i}].effects.${id} must be a finite number`);
                }
                effects[id] = stacks;
            }
        }
        const weaponRaw = node["weapon"];
        if (weaponRaw !== undefined &&
            (weaponRaw === null || typeof weaponRaw !== "object" || Array.isArray(weaponRaw))) {
            return fail(`observed.damage[${i}].weapon must be an object when present`);
        }
        const weaponNode = (weaponRaw ?? {});
        const weapon = {
            ...(typeof weaponNode["baseType"] === "string" ? { baseType: weaponNode["baseType"] } : {}),
            ...(typeof weaponNode["name"] === "string" ? { name: weaponNode["name"] } : {}),
            ...optionalNumber(weaponNode, "hotbarSlot", i, fail),
        };
        return {
            spellId,
            ...optionalNumber(node, "baseValue", i, fail),
            ...optionalNumber(node, "hit", i, fail),
            ...optionalNumber(node, "crit", i, fail),
            ...optionalNumber(node, "totalCombined", i, fail),
            ...(typeof node["wasCrit"] === "boolean" ? { wasCrit: node["wasCrit"] } : {}),
            ...(perSecond === undefined ? {} : { ailmentPerSecond: ailments }),
            ...(logRaw === undefined ? {} : { log }),
            ...(ailmentsRaw === undefined ? {} : { ailments: ailmentEvents }),
            ...(readingSource === undefined ? {} : { source: readingSource }),
            ...(effectsRaw === undefined ? {} : { effects }),
            ...(weaponRaw === undefined ? {} : { weapon }),
            ...(typeof node["targetNotes"] === "string" ? { targetNotes: node["targetNotes"] } : {}),
        };
    });
    // Provenance, not readings: a malformed row is dropped rather than failing the fixture, so
    // an older capture or a round trip that did not land still loads and still checks numbers.
    const sourcesRaw = observed["sources"];
    const sources = (Array.isArray(sourcesRaw) ? sourcesRaw : [])
        .map((raw) => {
        if (raw === null || typeof raw !== "object" || Array.isArray(raw))
            return undefined;
        const node = raw;
        const statId = node["statId"];
        const value = node["value"];
        if (typeof statId !== "string" || statId.length === 0)
            return undefined;
        if (typeof value !== "number" || !Number.isFinite(value))
            return undefined;
        return {
            statId,
            ctx: typeof node["ctx"] === "string" ? node["ctx"] : "",
            ...(typeof node["slot"] === "string" && node["slot"].length > 0 ? { slot: node["slot"] } : {}),
            type: typeof node["type"] === "string" ? node["type"] : "",
            value,
        };
    })
        .filter((row) => row !== undefined);
    return {
        name,
        ...(typeof root["notes"] === "string" ? { notes: root["notes"] } : {}),
        build: build,
        observed: {
            ...(sources.length === 0 ? {} : { sources }),
            source: source,
            capturedAt: asString(observed, "capturedAt"),
            mineAndSlashVersion: asString(observed, "mineAndSlashVersion"),
            packVersion: asString(observed, "packVersion"),
            ...(typeof observed["notes"] === "string" ? { notes: observed["notes"] } : {}),
            stats,
            ...(damageRaw === undefined ? {} : { damage }),
        },
    };
}
/**
 * One printed block — `Base Damage`, the `Damage Info:` rows, the `Multipliers:` rows, `Final
 * Damage`.
 *
 * Shared by `log[]` and by an ailment's two blocks, because the log prints all of them in the
 * same shape. `layers` and `moreMultis` default to empty rather than being required: an
 * `Ailment Proc:` block genuinely has none, and a capture that writes `"layers": []` is stating
 * that, not omitting it.
 */
function parseEvent(rawEvent, at, fail) {
    if (rawEvent === null || typeof rawEvent !== "object" || Array.isArray(rawEvent)) {
        return fail(`${at} must be an object`);
    }
    const ev = rawEvent;
    const element = ev["element"];
    if (typeof element !== "string" || element.length === 0) {
        return fail(`${at}.element must be a non-empty string`);
    }
    for (const key of ["baseDamage", "finalDamage"]) {
        if (typeof ev[key] !== "number" || !Number.isFinite(ev[key])) {
            return fail(`${at}.${key} must be a finite number`);
        }
    }
    const layersRaw = ev["layers"];
    if (layersRaw !== undefined && !Array.isArray(layersRaw)) {
        return fail(`${at}.layers must be an array when present`);
    }
    const layers = (layersRaw ?? []).map((rawLayer, k) => {
        if (rawLayer === null || typeof rawLayer !== "object" || Array.isArray(rawLayer)) {
            return fail(`${at}.layers[${k}] must be an object`);
        }
        const l = rawLayer;
        const layerId = l["layerId"];
        if (typeof layerId !== "string" || layerId.length === 0) {
            return fail(`${at}.layers[${k}].layerId must be a non-empty string`);
        }
        const side = l["side"];
        if (side !== "Source" && side !== "Target") {
            return fail(`${at}.layers[${k}].side must be "Source" or "Target"`);
        }
        // Exactly one of the two, because the log prints exactly one: `+N` for an ADD layer and
        // `xN` for every other action. A row carrying both is a transcription that guessed.
        const hasAmount = l["amount"] !== undefined;
        const hasMulti = l["multiplier"] !== undefined;
        if (hasAmount === hasMulti) {
            return fail(`${at}.layers[${k}] needs exactly one of "amount" or "multiplier"`);
        }
        return {
            layerId,
            side,
            ...(typeof l["element"] === "string" ? { element: l["element"] } : {}),
            ...optionalNumber(l, "amount", k, fail),
            ...optionalNumber(l, "multiplier", k, fail),
        };
    });
    const moreRaw = ev["moreMultis"];
    if (moreRaw !== undefined && !Array.isArray(moreRaw)) {
        return fail(`${at}.moreMultis must be an array when present`);
    }
    const moreMultis = (moreRaw ?? []).map((rawMore, k) => {
        if (rawMore === null || typeof rawMore !== "object" || Array.isArray(rawMore)) {
            return fail(`${at}.moreMultis[${k}] must be an object`);
        }
        const m = rawMore;
        const statId = m["statId"];
        if (typeof statId !== "string" || statId.length === 0) {
            return fail(`${at}.moreMultis[${k}].statId must be a non-empty string`);
        }
        if (typeof m["multi"] !== "number" || !Number.isFinite(m["multi"])) {
            return fail(`${at}.moreMultis[${k}].multi must be a finite number`);
        }
        return { statId, multi: m["multi"] };
    });
    return {
        element,
        baseDamage: ev["baseDamage"],
        layers,
        moreMultis,
        finalDamage: ev["finalDamage"],
    };
}
function optionalNumber(node, key, index, fail) {
    const v = node[key];
    if (v === undefined)
        return {};
    if (typeof v !== "number" || !Number.isFinite(v)) {
        fail(`[${index}].${key} must be a finite number`);
    }
    return { [key]: v };
}
function asString(node, key) {
    const v = node[key];
    return typeof v === "string" ? v : "";
}
//# sourceMappingURL=fixture.js.map